import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
  OnDestroy,
  Input,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import * as d3 from 'd3';
import * as topojson from 'topojson-client';
import usData from './us.json';
import { ThemeService, Theme } from '../../../../core/services/theme.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-nation-chart',
  templateUrl: './nation-chart.component.html',
  styleUrls: ['./nation-chart.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [],
})
export class NationChartComponent
  implements AfterViewInit, OnDestroy, OnChanges
{
  @Input() primaryLineColor: string = '';
  @Input() stateData: {}[] = [];
  @Input() valueKeys: {} | any = {
    keyName: 'state',
    valueKey: 'count',
  };

  @ViewChild('nationchartContainer', { static: true })
  private chartContainer!: ElementRef;

  private overlayGroup: d3.Selection<
    SVGGElement,
    unknown,
    null,
    undefined
  > | null = null;

  private resizeObserver!: ResizeObserver;
  private themeSubscription?: Subscription;
  private currentTheme: Theme = 'light';
  private svg!: d3.Selection<SVGSVGElement, unknown, null, undefined>;
  private g!: d3.Selection<SVGGElement, unknown, null, undefined>;
  private states: any;
  private labels: any; // ✅ text labels

  private width = 975; // default size (for viewBox)
  private height = 610;
  set: boolean = false;

  private themeService = inject(ThemeService);

  ngAfterViewInit(): void {
    // Subscribe to theme changes
    this.currentTheme = this.themeService.getTheme();
    this.themeSubscription = this.themeService.theme$.subscribe((theme) => {
      this.currentTheme = theme;
      this.redraw(); // Redraw chart with new theme colors
    });

    this.resizeObserver = new ResizeObserver(() => {
      this.redraw();
    });
    this.resizeObserver.observe(this.chartContainer.nativeElement);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    this.svg?.remove();
  }

  // Get theme-aware colors
  private getThemeColors() {
    const isDark = this.currentTheme === 'dark';
    return {
      // State colors
      stateFill: this.primaryLineColor || (isDark ? '#a78bfa' : '#8b5cf6'), // purple-400 / purple-600
      stateHoverFill: isDark ? '#818cf8' : '#6366f1', // indigo-400 / indigo-500
      selectedStateFill: isDark ? '#a78bfa' : '#8b5cf6', // blue-500 / blue-600
      stateBorder: isDark ? '#f4f4f5' : '#ffffff', // slate-100 / white
      
      // Text colors
      labelText: isDark ? '#f4f4f5' : '#ffffff', // slate-100 / white
      overlayText: isDark ? '#f4f4f5' : '#ffffff', // slate-100 / white
      
      // Dim opacity for non-selected states
      dimOpacity: isDark ? 0.4 : 0.5,
    };
  }

  private drawChart(): void {
    const container = this.chartContainer.nativeElement;
    const containerWidth = container.clientWidth;
    const containerHeight =
      container.clientHeight || (containerWidth * this.height) / this.width;

    // Get theme colors
    const colors = this.getThemeColors();

    const path = d3.geoPath();

    this.svg = d3
      .select(container)
      .append('svg')
      .attr('viewBox', [0, 0, this.width, this.height].toString())
      .attr('preserveAspectRatio', 'xMidYMid meet') // ✅ keep aspect ratio
      .style('width', `${containerWidth}px`)
      .style('height', `${containerHeight}px`)
      .style('max-width', '100%')
      .style('max-height', '100%')
      .style('transform', 'rotate(357deg)');

    this.g = this.svg.append('g');
    const features = (topojson.feature(
      usData as any,
      (usData as any).objects.states
    ) as any).features;

    this.states = this.g
      .append('g')
      .attr('fill', colors.stateFill)
      .attr('cursor', 'pointer')
      .selectAll('path')
      .data(features)
      /* .data(
        topojson.feature(usData as any, (usData as any).objects.states).features
      ) */
      .join('path')
      .attr('d', path as any)
      .on('click', (event, d) => this.clicked(event, d));

    // this.states.append('title').text((d: any) => d.properties.name);

    // ✅ add labels at centroids
    this.labels = this.g
      .append('g')
      .selectAll('text')
      .data(features)
      .join('text')
      .attr('class', 'state-label')
      .attr('x', (d: any) => path.centroid(d)[0])
      .attr('y', (d: any) => path.centroid(d)[1])
      .attr('text-anchor', 'middle')
      .attr('dy', '.35em')
      .style('font-size', '18px')
      .style('font-weight', '700')
      .style('fill', colors.labelText)
      .style('pointer-events', 'none')
      .text((d: any) => {
        const match: any = this.stateData.find((s: any) => {
          if (s[this.valueKeys.keyName] === d.properties.name) return s;
        });
        return match ? match[this.valueKeys.valueKey].toString() : '';
      });

    // borders
    this.g
      .append('path')
      .attr('fill', 'none')
      .attr('stroke', colors.stateBorder)
      .attr('stroke-linejoin', 'round')
      .attr(
        'd',
        path(
          topojson.mesh(
            usData as any,
            (usData as any).objects.states,
            (a: any, b: any) => a !== b
          )
        ) as any
      );
  }

  private clicked(event: any, d: any): void {
    if (this.set === true) {
      return this.reset();
    }
    event.stopPropagation();

    // Get theme colors
    const colors = this.getThemeColors();

    const path = d3.geoPath();
    const dPath = path(d) as string;

    // dim other states
    this.states.transition().duration(250).style('fill-opacity', colors.dimOpacity);
    this.labels.style('opacity', (dd: any) => (dd.id === d.id ? 1 : 0));

    // hide original state's fill
    const clickedElement = d3.select(event.currentTarget);
    clickedElement.style('fill-opacity', 0);

    // remove any existing overlay
    this.clearOverlay();

    // create overlay group
    this.set = true;
    this.overlayGroup = this.g
      .append('g')
      .attr('class', 'overlay-layer')
      .style('pointer-events', 'none');

    // append overlay path
    const overlay = this.overlayGroup
      .append('path')
      .attr('d', dPath)
      .attr('fill', colors.selectedStateFill)
      .attr('stroke', 'none')
      .attr('fill-opacity', 0);

    // get bounding box of overlay path
    const bbox = (overlay.node() as SVGPathElement).getBBox();

    // get SVG container dimensions
    const svgWidth = this.chartContainer.nativeElement.clientWidth;
    const svgHeight = this.chartContainer.nativeElement.clientHeight;

    // calculate center positions
    const svgCenterX = svgWidth / 2;
    const svgCenterY = svgHeight / 2;

    const shapeCenterX = bbox.x + bbox.width / 5;
    const shapeCenterY = bbox.y + bbox.height / 2.5;

    // scale factor
    const scale = 2;

    // calculate translate so that shape is centered
    const translateX = svgCenterX - shapeCenterX * scale;
    const translateY = svgCenterY - shapeCenterY * scale;

    // apply transform
    this.overlayGroup.attr(
      'transform',
      `translate(${translateX},${translateY}) scale(${scale})`
    );

    // fade in overlay
    overlay.transition().duration(400).attr('fill-opacity', 1);

    // Get theme colors for text
    const textColors = this.getThemeColors();

    // ---- ADD TEXT HERE ----
    this.overlayGroup
      .append('text')
      .attr('x', bbox.x + bbox.width / 2)
      .attr('y', bbox.y + bbox.height / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('fill', textColors.overlayText)
      .style('font-size', '16px')
      .style('font-weight', 'bold')
      .text(() => {
        const match: any = this.stateData.find(
          (s: any) => s[this.valueKeys.keyName] === d.properties.name
        );
        if (match) {
          return (
            (match[this.valueKeys.valueKey] || '0').toString() +
            ` \n (${match[this.valueKeys.keyName].toString()})`
          );
        } else {
          return `0 (${d.properties.name})`;
        }
      });
  }

  // remove overlay and reset states
  private reset(): void {
    // if you used svg.on('click', reset) ensure this only fires when clicking background
    this.set = false;
    this.clearOverlay();

    this.labels.style('opacity', 1);
  }

  // helper to clear overlay and restore original clicked state's fill
  private clearOverlay(): void {
    if (this.overlayGroup) {
      this.overlayGroup.remove();
      this.overlayGroup = null;
    }
    // ensure all original states show fill again (in case one was hidden)
    this.states.style('fill-opacity', 1);
  }

  private redraw(): void {
    // 👀 clear and redraw
    this.svg?.remove();
    this.drawChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.redraw();
  }
}
